<template>
    <div class="form-row align-items-center" :class="{ disabled: disabled }">
        <div class="col-12">
            <label for="quantity">{{ dictionary.quantity }}</label>
        </div>

        <div class="col-12 mb-2" :class="{ 'col-md-auto': !singleColumn }">
            <div class="input-group">
                <input id="quantity"
                       type="number"
                       min="0"
                       :step="unit.stepSize"
                       class="form-control"
                       v-model.number="qty"
                       value="0"
                       :disabled="disabled" />
                <div class="input-group-append position-relative">
                    <span class="input-group-text px-2">{{ units }}</span>
                    <button v-if="product.price.unit === 'sack'" class="btn btn-primary badge rounded-pill question-tooltip bg-primary text-light p-2 mx-2 with-tooltip">
                        ?
                        <span class="tooltiptext">{{ dictionary.sackDefinition }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="qty > 0"
             class="text-center col-12 mb-2"
             :class="{ 'col-md-auto': !singleColumn }">
            <p class="m-0">
                {{ price }} +
                <span v-if="calculatingDelivery">
                    <span v-if="calculatingDelivery"
                          class="spinner-border spinner-border-sm align-middle text-light"
                          role="status"></span>
                    {{ dictionary.calculatingDelivery }}
                </span>
                <span v-else>
                    {{ deliveryPrice }}
                    {{ dictionary.delivery }}
                </span>
            </p>
        </div>

        <div class="col-12 mb-2"
             :class="{ 'col-md-auto ml-md-auto': !singleColumn }">
            <button class="btn"
                    type="button"
                    :class="buttonClass"
                    :disabled="buttonDisabled"
                    v-on:click="addToCart">
                {{ dictionary.addToCart }}
                <div v-if="addingToCart"
                     class="spinner-border spinner-border-sm align-middle text-light ml-2"
                     role="status">
                    <span class="sr-only">{{ dictionary.addingToCart }}</span>
                </div>
            </button>
        </div>

        <div class="col-12">
            <p class="text-success mb-0 mb-2" v-if="addedToCartMessage">
                {{ addedToCartMessage }}
            </p>
        </div>

        <div class="col-12" v-if="deliveryWarning">
            <p class="text-danger mb-0 mb-2">
                {{ dictionary.deliveryWarning }}
            </p>
        </div>
    </div>
</template>

<script>
    import log from "loglevel";
    import CartService from "../services/cart.service";
    import Units from "../modules/units";
    import { GetShippingPrice } from "../services/shipping.service";
    import { StoreCurrency, DisplayPricesWithTax } from "../services/store.service";
    import { GetTranslations, Translate } from "../services/translation.service";

    export default {
        name: "QuantityField",
        props: {
            product: {
                type: Object,
                required: true,
            },
            quantity: {
                type: Number,
                required: false,
                default: 0,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            singleColumn: {
                type: Boolean,
                default: false,
            },
            closestPlant: {
                type: Object,
                required: false,
            },
        },
        data() {
            return {
                qty: null,
                addingToCart: false,
                addedToCartMessage: "",
                unit: Units[this.product.price.unit],
                dictionary: GetTranslations("addToCart"),
                calculatingDelivery: false,
                deliveryPrice: "",
                deliveryWarning: false
            };
        },
        computed: {
            buttonDisabled: function () {
                var me = this;
                if (me.qty > 0 && !me.addingToCart) {
                    return false;
                }
                return true;
            },
            buttonClass: function () {
                if (!this.disabled && this.qty > 0) {
                    return "btn-secondary";
                }
                return "btn-outline-light";
            },
            price: function () {
                let me = this;
                let unitPrice = DisplayPricesWithTax
                    ? me.product.price.withTax
                    : me.product.price.withoutTax;
                var price = me.qty * unitPrice;
                let priceString = `${me.dictionary.price} ${StoreCurrency.format(price)}`;
                return priceString;
            },
            units: function () {
                let unitEnum = this.product.price.unit;
                if (this.qty !== 1) {
                    return Translate(unitEnum + "Plural", "units");
                }
                return Translate(unitEnum, "units");
            },
        },
        methods: {
            copyQtyFromProp: function () {
                
                this.qty = this.unit.round(this.quantity);
            },
            updateQty: function () {
                var { product, qty } = this;
                var me = this;
                me.qty = me.unit.round(qty);
                me.calculateDelivery();
                me.$emit("update-qty", product.productReference, qty);
            },
            addToCart: function () {
                var me = this;
                me.addingToCart = true;
                var { productReference } = me.product;
                me.$emit("adding-to-cart", productReference);
                CartService.addCartItem(productReference, me.qty)
                    .then((response) => {
                        if (response.ok) {
                            me.addedToCartMessage =
                                `${me.qty} ${Translate(me.product.price.unit + "Plural", "units")} ` +
                                me.dictionary.addedToCart;
                            me.qty = null;
                        }
                    })
                    .finally(() => (me.addingToCart = false));
            },
            calculateDelivery: function () {
                var me = this;
                if (me.closestPlant) {
                    this.calculatingDelivery = true;
                    log.info("Fetching delivery price");
                    // TODO - debounce this?
                    GetShippingPrice(
                        me.product.shippingMethodSku,
                        me.closestPlant.distance,
                        me.qty || 0
                    ).then((deliveryPrice) => {
                        log.info("Fetched delivery price", deliveryPrice);
                        let truckPrice = StoreCurrency.format(deliveryPrice.unitCost);
                        me.deliveryPrice = `${deliveryPrice.units} × ${truckPrice}`;
                        me.calculatingDelivery = false;

                        me.deliveryWarning = deliveryPrice.units > 1;
                    });
                } else {
                    me.deliveryWarning = me.qty > 10;
                }
            },
        },
        watch: {
            quantity() {
                this.copyQtyFromProp();
            },
            qty() {
                this.updateQty();
            },
        },
        beforeMount() {
            this.copyQtyFromProp();
        },
    };
</script>